/**
 * 图片压缩工具 — 基于 browser-image-compression
 *
 * 压缩策略（分级，参考业内最佳实践）：
 *   1. 小图（≤ 1MB）：跳过压缩，直接返回
 *   2. 中图（1-10MB）：保质压缩 — 缩分辨率到 2048px，initialQuality 0.8
 *   3. 大图（> 10MB）或保质压缩后仍 > 10MB：强制压到 10MB 以下
 *
 * 支持 AbortController 取消 + onProgress 进度回调。
 * 后端硬限制 10MB，前端保证上传文件不超过此限制。
 */

import imageCompression from "browser-image-compression"

/** 不需要压缩的阈值（MB） */
const SKIP_THRESHOLD_MB = 1
/** 后端硬限制（MB） */
const BACKEND_LIMIT_MB = 10
/** 保质压缩的最大分辨率（px） */
const QUALITY_MAX_DIMENSION = 2048
/** 保质压缩的初始质量（0.6 以上肉眼几乎无差别） */
const INITIAL_QUALITY = 0.8

interface CompressParams {
  /** AbortSignal，用于取消压缩 */
  signal?: AbortSignal
  /** 进度回调（0-100），两轮压缩合并为统一进度 */
  onProgress?: (percent: number) => void
}

/**
 * 压缩图片文件（分级策略）
 *
 * @param file - 原始图片文件
 * @param params - signal / onProgress
 * @returns 压缩后的 File，取消时抛出 AbortError
 */
export async function compressImage(
  file: File,
  params?: CompressParams,
): Promise<File> {
  const { signal, onProgress } = params ?? {}
  const fileSizeMB = file.size / (1024 * 1024)

  // 小图：跳过压缩
  if (fileSizeMB <= SKIP_THRESHOLD_MB) {
    onProgress?.(100)
    return file
  }

  // 第一轮：保质压缩（缩分辨率 + 高质量）
  const qualityCompressed = await imageCompression(file, {
    maxWidthOrHeight: QUALITY_MAX_DIMENSION,
    initialQuality: INITIAL_QUALITY,
    useWebWorker: true,
    preserveExif: true,
    fileType: file.type as "image/jpeg" | "image/png" | "image/webp",
    signal,
    onProgress: (p) => {
      // 第一轮占 0-80% 进度
      onProgress?.(Math.round(p * 0.8))
    },
  })

  const afterQualitySizeMB = qualityCompressed.size / (1024 * 1024)

  // 保质压缩后已在限制内
  if (afterQualitySizeMB <= BACKEND_LIMIT_MB) {
    onProgress?.(100)
    return new File([qualityCompressed], file.name, { type: qualityCompressed.type })
  }

  // 第二轮：强制压到 10MB 以下（循环降质）
  const forceCompressed = await imageCompression(qualityCompressed, {
    maxSizeMB: BACKEND_LIMIT_MB,
    maxWidthOrHeight: QUALITY_MAX_DIMENSION,
    useWebWorker: true,
    fileType: qualityCompressed.type as "image/jpeg" | "image/png" | "image/webp",
    signal,
    onProgress: (p) => {
      // 第二轮占 80-100% 进度
      onProgress?.(80 + Math.round(p * 0.2))
    },
  })

  onProgress?.(100)
  return new File([forceCompressed], file.name, { type: forceCompressed.type })
}
